import * as THREE from 'three';
import { box, DARK } from './blocky.js';

const SKIN = '#f5d142';
const SHORTS = '#1b2430';
const SHOE = '#f5f5f5';

export type AthletePose =
  | 'idle'
  | 'set'
  | 'run'
  | 'javelin-run'
  | 'finish'
  | 'jump'
  | 'flop'
  | 'land'
  | 'sit'
  | 'windup'
  | 'throw';

/**
 * Blocky athlete, 1.9 m tall, built facing +Z in its own space and turned to face +X (the
 * running direction). Limbs are pivot groups so the run cycle is a few rotations per frame.
 */
export class Athlete {
  readonly root = new THREE.Group();
  private body = new THREE.Group();
  private torso: THREE.Mesh;
  private hipL = new THREE.Group();
  private hipR = new THREE.Group();
  private shoulderL = new THREE.Group();
  private shoulderR = new THREE.Group();
  private shoes: THREE.Mesh[] = [];
  private footBounds = new THREE.Box3();
  private phase = 0;
  private lastPhase = 0;
  private pose: AthletePose = 'idle';
  private blend = 0;
  /** Number on the bib, for later. */
  bib = 0;

  constructor(kit: string) {
    // Legs: pivot at the hip (y = 0.85), leg hangs down.
    for (const [hip, side] of [
      [this.hipL, -1],
      [this.hipR, 1],
    ] as const) {
      // Hip at 0.85; the whole leg including the shoe spans exactly 0.85 so the sole sits on
      // the ground when standing.
      hip.position.set(side * 0.17, 0.85, 0);
      hip.add(box(0.26, 0.5, 0.28, SKIN, 0, -0.25, 0, 0.06)); // thigh
      hip.add(box(0.24, 0.28, 0.26, SKIN, 0, -0.63, 0, 0.05)); // shin
      const shoe = box(0.26, 0.14, 0.38, SHOE, 0, -0.78, 0.06, 0.04);
      this.shoes.push(shoe);
      hip.add(shoe);
      this.body.add(hip);
    }
    this.body.add(box(0.62, 0.3, 0.34, SHORTS, 0, 0.98, 0, 0.06)); // shorts
    this.torso = box(0.62, 0.55, 0.32, kit, 0, 1.38, 0, 0.07); // vest
    this.body.add(this.torso);
    this.body.add(box(0.4, 0.42, 0.4, SKIN, 0, 1.9, 0, 0.1)); // head
    this.body.add(box(0.44, 0.1, 0.44, DARK, 0, 2.12, 0, 0.03)); // hair
    // Face towards +Z.
    this.body.add(box(0.07, 0.07, 0.04, DARK, -0.09, 1.95, 0.21, 0));
    this.body.add(box(0.07, 0.07, 0.04, DARK, 0.09, 1.95, 0.21, 0));
    this.body.add(box(0.16, 0.04, 0.04, DARK, 0, 1.8, 0.21, 0));
    // Arms: pivot at the shoulder (y = 1.6).
    for (const [sh, side] of [
      [this.shoulderL, -1],
      [this.shoulderR, 1],
    ] as const) {
      sh.position.set(side * 0.42, 1.6, 0);
      sh.add(box(0.2, 0.3, 0.2, kit, 0, -0.1, 0, 0.05)); // sleeve
      sh.add(box(0.18, 0.5, 0.18, SKIN, 0, -0.45, 0, 0.05)); // arm
      sh.add(box(0.2, 0.16, 0.2, SKIN, 0, -0.76, 0, 0.04)); // hand
      this.body.add(sh);
    }
    this.body.rotation.order = 'YXZ';
    this.body.rotation.y = Math.PI / 2; // face +X
    this.root.add(this.body);
  }

  setKit(hex: string): void {
    // Only the vest and sleeves carry the kit colour; recolour instead of rebuilding.
    const c = new THREE.Color(hex);
    for (const obj of [this.torso, this.shoulderL.children[0], this.shoulderR.children[0]]) {
      const mesh = obj as THREE.Mesh | undefined;
      if (mesh) mesh.material = new THREE.MeshLambertMaterial({ color: c });
    }
  }

  setPose(p: AthletePose): void {
    if (p !== this.pose) {
      this.pose = p;
      this.blend = 0;
    }
  }

  get currentPose(): AthletePose {
    return this.pose;
  }

  /** Place on the ground: x along the straight, lane centre z. */
  place(x: number, z: number): void {
    this.root.position.x = x;
    this.root.position.z = z;
  }

  /** Face a direction of travel (unit vector in the ground plane); default is +x. */
  face(dx: number, dz: number): void {
    this.root.rotation.y = Math.atan2(-dz, dx);
  }

  /** Height of the root (the feet, in upright poses) above the ground. */
  setHeight(y: number): void {
    this.root.position.y = y;
  }

  /** After animating a grounded athlete, place the lowest sole on the surface. */
  groundAt(surfaceY: number): void {
    this.root.position.y = 0;
    this.root.updateWorldMatrix(true, true);
    this.footBounds.makeEmpty();
    // Cached geometry bounds avoid scanning bevel vertices on every frame.
    for (const shoe of this.shoes) this.footBounds.expandByObject(shoe);
    this.root.position.y = surfaceY - this.footBounds.min.y;
  }

  /** World-space grip point after animation and floor alignment. */
  throwingHand(target: THREE.Vector3): THREE.Vector3 {
    return this.shoulderR.localToWorld(target.set(0, -0.76, 0));
  }

  /**
   * Offset from the root to the centre of mass for the current pose, in world units along
   * the running axis and up. Callers that track a simulated centre of mass place the root
   * at `com - anchor`. Lying poses (flop, sit) are horizontal, so the CoM sits ~1 m along
   * the body and barely above the root; upright poses put it a metre up.
   */
  anchor(): { dx: number; dy: number } {
    // Blended with the pose transition so the body never jumps when it starts to lie down.
    return { dx: 0.99 * this.lie, dy: 1.0 + (0.17 - 1.0) * this.lie };
  }
  private lie = 0;

  /**
   * Animate for one frame. `v` is speed (m/s). Returns the number of foot strikes this
   * frame so the caller can play footsteps in time with the animation.
   */
  update(dt: number, v: number, topSpeed: number): number {
    this.blend = Math.min(1, this.blend + dt * 4);
    const k = 1 - Math.exp(-dt * 10);
    const speed01 = Math.min(1, v / topSpeed);
    let strikes = 0;

    // Target limb angles per pose. Yaw is +X (running direction) except when the jumper
    // turns his back to the bar for the flop.
    let legL = 0,
      legR = 0,
      armL = 0,
      armR = 0,
      lean = 0,
      bob = 0,
      drop = 0,
      armOut = 0.08;
    let yaw = Math.PI / 2;
    if (this.pose === 'run' || this.pose === 'javelin-run' || (this.pose === 'finish' && v > 0.5)) {
      const stride = 1.0 + 0.11 * v;
      const cycles = v > 0.05 ? (v / stride) * dt : 0;
      this.lastPhase = this.phase;
      this.phase += cycles * Math.PI * 2;
      // A foot lands each half cycle.
      strikes = Math.floor(this.phase / Math.PI) - Math.floor(this.lastPhase / Math.PI);
      const amp = 0.25 + 0.6 * speed01;
      const s = Math.sin(this.phase);
      legL = s * amp;
      legR = -s * amp;
      armL = -s * amp * 0.9 - 0.3;
      armR = s * amp * 0.9 - 0.3;
      lean = 0.08 + 0.14 * speed01;
      bob = Math.abs(Math.cos(this.phase)) * 0.06 * speed01;
      armOut = 0.15;
      // Keep the throwing hand above the shoulder while the legs and free arm run.
      if (this.pose === 'javelin-run') armR = -2.55;
    } else if (this.pose === 'set') {
      // Crouched in the blocks: one leg forward, one back, hands on the line.
      legL = -1.15;
      legR = 0.55;
      armL = -1.65;
      armR = -1.65;
      lean = 0.95;
      drop = -0.42;
      armOut = 0.12;
    } else if (this.pose === 'finish') {
      // Arms up, catching breath.
      armL = -2.9;
      armR = -2.9;
      armOut = 0.35;
      lean = 0.02;
    } else if (this.pose === 'jump') {
      // Long jump hang: legs swung forward, arms reaching.
      legL = -1.3;
      legR = -1.1;
      armL = -2.4;
      armR = -2.4;
      armOut = 0.3;
      lean = -0.15;
    } else if (this.pose === 'flop') {
      // Fosbury flop: back to the bar, head first, body horizontal and arched. Negative leg
      // angles bring the knees up towards the sky; positive would dangle them through the bar.
      yaw = -Math.PI / 2;
      legL = -0.9;
      legR = -0.7;
      armL = -2.6;
      armR = -2.6;
      armOut = 0.6;
      lean = -1.4;
    } else if (this.pose === 'land') {
      // Deep crouch: landing in the sand, and the coil before a jump. The body drops and
      // leans as the legs swing forward, and those add up at the feet: hip height is
      // 0.85·cos(lean) + drop and the foot sits 0.85·cos(lean + leg) below that, so these
      // values put the soles on the ground rather than 0.4 m under it.
      legL = -1.3;
      legR = -1.25;
      armL = -0.8;
      armR = -0.8;
      lean = 0.35;
      drop = -0.3;
    } else if (this.pose === 'windup') {
      // Shot tucked at the neck, crouched and coiled; the crouch deepens with the power pump.
      const t = performance.now() / 1000;
      legL = -0.5;
      legR = 0.4;
      armL = -0.3;
      armR = -2.4;
      armOut = 0.1;
      lean = 0.35 + Math.sin(t * 9) * 0.05 * speed01;
      drop = -0.25;
    } else if (this.pose === 'throw') {
      // Fully extended after the put: arm out and up, chest open, back leg trailing.
      legL = -0.9;
      legR = 0.6;
      armL = 0.4;
      armR = -2.2;
      armOut = 0.5;
      lean = -0.25;
    } else if (this.pose === 'sit') {
      // Flat on the mat, on his back, knees up.
      yaw = -Math.PI / 2;
      legL = -1.2;
      legR = -1.0;
      armL = -1.6;
      armR = -1.6;
      armOut = 0.5;
      lean = -1.5;
      drop = 0.1;
    } else {
      // Idle: slight sway.
      const t = performance.now() / 1000;
      armL = -0.1 + Math.sin(t * 1.3) * 0.05;
      armR = -0.1 - Math.sin(t * 1.3) * 0.05;
      bob = Math.sin(t * 2) * 0.01;
    }

    const lerp = (a: number, b: number) => a + (b - a) * k;
    this.lie = lerp(this.lie, this.pose === 'flop' || this.pose === 'sit' ? 1 : 0);
    this.hipL.rotation.x = lerp(this.hipL.rotation.x, legL);
    this.hipR.rotation.x = lerp(this.hipR.rotation.x, legR);
    this.shoulderL.rotation.x = lerp(this.shoulderL.rotation.x, armL);
    this.shoulderR.rotation.x = lerp(this.shoulderR.rotation.x, armR);
    this.shoulderL.rotation.z = lerp(this.shoulderL.rotation.z, armOut);
    this.shoulderR.rotation.z = lerp(this.shoulderR.rotation.z, -armOut);
    this.body.rotation.x = lerp(this.body.rotation.x, lean);
    this.body.rotation.y = lerp(this.body.rotation.y, yaw);
    this.body.position.y = lerp(this.body.position.y, bob + drop);
    return strikes;
  }
}
